/**
 * Server-side tracking for long-running work a user started and then walked
 * away from.
 *
 * The browser already has a background-jobs panel, but it only knows about work
 * this tab started: close the tab and the job is gone, with no way to tell a
 * finished upload from an abandoned one. These rows are the durable counterpart
 * — the thing a reload, a new tab, or a notification can read.
 *
 * Execution lives in Temporal; a row here is the projection of that run which
 * the UI can actually query. `wfv2_workflow_runs` plays the same role for the
 * workflow engine, but it is bound to a user-defined `WorkflowDefinition`, so it
 * cannot carry internal work like this.
 */

/**
 * Where a job has got to.
 *
 * `Queued` means accepted but not yet picked up, which is a real state here: the
 * row is written in the same transaction that persists the upload, before the
 * workflow that does the work has started.
 */
export enum BackgroundJobStatus {
  Queued = "queued",
  Running = "running",
  Succeeded = "succeeded",
  Failed = "failed",
}

/** True once the job has reached a state it will not leave on its own. */
export function isBackgroundJobSettled(status: BackgroundJobStatus): boolean {
  return (
    status === BackgroundJobStatus.Succeeded ||
    status === BackgroundJobStatus.Failed
  );
}

/**
 * What kind of work a job represents.
 *
 * Stored as a plain string rather than a Postgres enum, matching
 * `comm_notification_events.event_type`: a new kind of background work should
 * not need a migration, and the consumers that care about a specific kind
 * already have to handle unrecognized ones (an older tab polling after a deploy
 * has shipped a new kind).
 */
export enum BackgroundJobKind {
  /** Source collection and candidate generation for a review-first SOP import. */
  SopImport = "sop_import",
  PricelistExtraction = "pricelist_extraction",
  /**
   * A Ship & Debit 844 "Batch and Send". Large divisions (W/+ spans ~100 DUNS
   * batches) take several minutes — past the browser's ~300s fetch limit — so
   * the send runs in Temporal and the staged page polls the job. The full send
   * result (successful/failed/submissions) is stored on the job's payload under
   * `result` when it succeeds.
   */
  Sd844BatchSend = "sd_844_batch_send",
  /**
   * A Ship & Debit 849 rejection resubmission: clone the disputed 844
   * transactions and send them as a NEW 844 submission. Same shape of work as
   * `Sd844BatchSend` (it ends in the same `submitTransactions` call), so it
   * runs in Temporal for the same reason — a resubmission spanning many
   * transactions and DUNS locations is minutes of work, and a request that
   * times out mid-send leaves the user clicking Resubmit on a claim that
   * already went out. The result is stored on the payload under `result`.
   */
  Sd849Resubmit = "sd_849_resubmit",
  /**
   * RETIRED (#7656) — kept so historic job rows still read. Nothing enqueues it:
   * an 849 now opens only for the claims an email answers, from the review
   * session. What it was:
   *
   * Creating (or attaching) the 849 for an email a reviewer labelled `is_849` on
   * the Ship & Debit labelling page.
   *
   * Moved off the request because it is NOT quick: the apply path resolves the
   * supplier, parses every Excel attachment, matches the parsed rows to sent 844
   * lines and hunts for an existing 849 this email might belong to. Measured on
   * Rexel prod it ran 22–47s, and the S&D team works on ~0.25 Mbps links where a
   * request that long is indistinguishable from a dead one — the reviewer gave
   * up, clicked again, and got a second 849 for the same claim.
   *
   * The job is keyed to the inbound email (`entityType: "inbound_email"`), which
   * is what makes a second click return the SAME job instead of starting another
   * run. The payload carries `{ inboundEmailId, action }` and, on success,
   * `{ result }` with the reconciliation outcome + the response id to link to.
   */
  Sd849CreateFromLabel = "sd_849_create_from_label",
  /**
   * A CSV → XLSX conversion through the Item Master utility. The upload is
   * staged to blob storage by the browser; a Temporal workflow streams it
   * through the converter and stores the result as a document. On success the
   * job's `entityId` is the result document (xlsx, or csv when Excel would have
   * been a multi-part zip — GEN-2665) and the payload carries
   * `{ fileName, xlsxDocumentId, rowCount, fileCount, outputKind }`.
   */
  CsvToXlsxConversion = "csv_to_xlsx_conversion",
  /**
   * A Ship & Debit "GL Posting — 849 Responses" report run.
   *
   * Read-only, unlike every other kind here, and moved off the request for a
   * different reason than the send jobs: its query drives off the whole SENT
   * `sd_e844d` history (the date filter bounds only the 849 side), so a
   * wide-scope run is tens of minutes of DB work. Held on the request it
   * outlived the browser's fetch limit and left a spinner that could never
   * resolve while the connection burned on a web instance.
   *
   * The workbooks are written to blob storage rather than onto the payload —
   * they run to megabytes. The payload carries `{ result }` with each file's
   * `storageKey` plus the report warnings; a run slow enough that nobody is
   * still watching also emails the files to whoever asked for it.
   */
  Sd849GlPostingReport = "sd_849_gl_posting_report",
  /**
   * A generic file export (GEN-2683): a builder registered by kind streams the
   * file straight into blob storage, and the payload carries `{ result }` with
   * its `storageKey` and filename. The UI polls the job, then asks for a
   * short-lived signed link — the file never passes through a web response.
   */
  FileExport = "file_export",
  /**
   * A Hubbell Feedback run (AIA-28): one Results Workbook plus up to nine
   * Customer Sales Summaries, staged by the browser, become one Feedback File
   * per paired Division. One job is one run with many output files. At most
   * one run is active per organization (`entityType: "hubbell_feedback"`,
   * `entityId` = the organization), and a new run replaces the previous run's
   * files. The payload carries `{ inputs }` from the start, `{ progress }` as
   * each Division is reported, and `{ report }` once the run succeeds.
   */
  HubbellFeedback = "hubbell_feedback",
  /**
   * A Mass Renewal run (docs/pricing/mass-renewal-upload.md): a Hubbell or
   * ABB Results Workbook, one to nine filled-in Feedback Files and an optional
   * Exception List, staged by the browser, become per Division the AS400
   * upload CSV, the exceptions CSV and the Renewal Report. One job is one run
   * with many output files. At most one run is active per organization and
   * supplier (`entityType: "hubbell_mass_renewal"` / `"abb_mass_renewal"`,
   * `entityId` = the organization), and a new run replaces the previous run's
   * files. The payload carries `{ inputs, rules }` from the start (the rules
   * snapshotted so the report is reproducible), `{ progress }` as each
   * Division is built, and `{ report, outputs }` once the run succeeds.
   */
  MassRenewal = "mass_renewal",
}
